import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { newsAppHeader } from 'hb-react/frontoffice/apps/NewsIndex/blocks/newsAppHeader';
import { newsCategoryTags } from 'hb-react/frontoffice/apps/NewsIndex/blocks/newsCategoryTags';
import { newsPinnedItems } from 'hb-react/frontoffice/apps/NewsIndex/blocks/newsPinnedItems';
import { newsSearchFilters } from 'hb-react/frontoffice/apps/NewsIndex/blocks/newsSearchFilters';
import { newsSearchResults } from 'hb-react/frontoffice/apps/NewsIndex/blocks/newsSearchResults';
import type { Config } from 'hb-react/frontoffice/apps/NewsIndex/types';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import oneColumnTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/OneColumnTemplate';
import twoColumnsTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/TwoColumnsTemplate';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import {
  getAppPageConfig,
  getPageTitle,
} from 'hb-react/frontoffice/utils/page';
import getNewsHeaderProps from 'hb-react/shared/apps/News/utils/getNewsHeaderProps';
import { useAppHeaderVariant } from 'hb-react/shared/components/DesignSystem/AppHeader';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';

export const NewsIndexContainer = ({
  config,
  network,
  topic,
}: {
  config: Config;
  network?: any;
  topic?: any;
}) => {
  const breakpoint = useBreakpoint();

  const navigate = useNavigate();

  const { topicId, whitelabelName, whitelabelURL, topicName } =
    useTopicContext();

  const { flagAnalyticsChangeTitleTags } = useFeatureFlag();

  const appHeaderProps = getNewsHeaderProps({
    appPageConfig: config?.custom?.page,
  });

  const { coverPictureUrl, variant } = useAppHeaderVariant(appHeaderProps);

  const TEMPLATES = {
    [oneColumnTemplate.id]: oneColumnTemplate,
    [twoColumnsTemplate.id]: twoColumnsTemplate,
  };

  const templateId =
    config?.custom?.page?.config?.blocks_renderer_template ||
    twoColumnsTemplate.id;

  const appPageConfig = getAppPageConfig('news', null, topic?.app_pages);

  const menuName = appPageConfig?.menu_name || config?.custom?.page?.menu_name;

  const template = TEMPLATES[templateId];

  return (
    <>
      {flagAnalyticsChangeTitleTags ? (
        <DocumentTitle
          title={generatePageTitle({
            title: menuName || '',
            networkName: network?.page_title || network?.title,
            topicName: whitelabelName || topic?.name || topicName,
          })}
        />
      ) : (
        !topicId && (
          /* @ts-expect-error: untyped */
          <DocumentTitle title={getPageTitle({ featureName: 'news' })} />
        )
      )}

      <BlocksRenderer
        blocks={[
          newsAppHeader({ appHeaderProps }),
          newsSearchFilters({ breakpoint }),
          newsPinnedItems({
            config,
            coverPictureUrl,
            navigate,
            topicId,
            whitelabelURL,
          }),
          newsCategoryTags({ config }),
          newsSearchResults({
            breakpoint,
            config,
            coverPictureUrl,
            navigate,
            topicId,
            whitelabelURL,
          }),
        ]}
        templateComponent={template.component}
        templateId={template.id}
        templateProps={{
          hasFilters: true,
          topicId,
          variant,
        }}
        templateZones={template.zones}
      />
    </>
  );
};
